// New custom hook to handle calendar state logic
import { getTimeFormat } from '@utils/dateHelper';
import { format, isSameDay, isEqual, isBefore } from 'date-fns';
import { useCallback, useEffect, useState } from 'react';
import { formatTime, hasReservationConflict } from '../utils/calendarHelpers';
import type { Reservation, TimeSlot } from '@app-types/models/reservation';
import type { StylistAvailability, BusinessHours } from '@app-types/models/user';

export interface StylistData {
  availability: StylistAvailability;
  [key: string]: unknown;
}

export interface BusinessData {
  businessHours: BusinessHours;
  [key: string]: unknown;
}

interface GetSlotIndexParams {
  date?: Date | string | null;
  timeString?: string;
}

interface SelectedSlots {
  start: Date | null;
  end: Date | null;
}

interface RearrangeConfirmation {
  newStart: Date;
  newEnd: Date;
}

interface HandleMouseDownParams {
  dayIndex: number;
  slotIndex: number;
  reservation?: Reservation | null;
  slotBlock?: TimeSlot | null;
  isDayOff: boolean;
  rearrangeMode: boolean;
  rearrangingReservation?: Reservation;
  reservationData: Reservation[];
}

interface HandleMouseDownResultError {
  error: string;
}

interface HandleMouseDownResultReservation {
  type: 'reservation';
  reservation: Reservation;
}

interface HandleMouseDownResultSlotBlock {
  type: 'slotBlock';
  slotBlock: TimeSlot;
}

interface HandleMouseDownResultDrag {
  type: 'drag';
}

type HandleMouseDownResult =
  | HandleMouseDownResultError
  | HandleMouseDownResultReservation
  | HandleMouseDownResultSlotBlock
  | HandleMouseDownResultDrag
  | null;

interface HandleMouseEnterParams {
  dayIndex: number;
  slotIndex: number;
  isReserved: boolean;
  rearrangeMode: boolean;
}

interface HandleTouchStartParams {
  dayIndex: number;
  slotIndex: number;
  reservation?: Reservation | null;
  slotBlock?: TimeSlot | null;
  isDayOff: boolean;
  rearrangeMode: boolean;
  rearrangingReservation?: Reservation;
  reservationData: Reservation[];
}

export interface CalendarStateReturn {
  startDayOffset: number;
  setStartDayOffset: React.Dispatch<React.SetStateAction<number>>;
  calendar: boolean[][];
  setCalendar: React.Dispatch<React.SetStateAction<boolean[][]>>;
  getSlotIndex: (params: GetSlotIndexParams) => number;
  getSlotDate: (slotIndex: number, dayIndex: number) => Date;
  resetCalendarByStylistData: () => void;
  resetCalendar: () => void;
  getReservation: (dayIndex: number, slotIndex: number, reservationData: Reservation[]) => Reservation | undefined;
  getSlotBlock: (dayIndex: number, slotIndex: number) => TimeSlot | null;
  fillDiscontinuousSelectedSlots: (dayIndex: number, startSlotIndex: number, endSlotIndex: number) => void;
  isDragging: boolean;
  selectedSlots: SelectedSlots;
  showCreateModal: boolean;
  setShowCreateModal: React.Dispatch<React.SetStateAction<boolean>>;
  resetSelectedSlots: () => void;
  handleMouseDown: (params: HandleMouseDownParams) => HandleMouseDownResult;
  handleMouseEnter: (params: HandleMouseEnterParams) => void;
  handleMouseUp: () => void;
  getDayOff: (dayIndex: number, startDayOffset: number) => boolean;
  confirmRearrange: RearrangeConfirmation | null;
  setConfirmRearrange: React.Dispatch<React.SetStateAction<RearrangeConfirmation | null>>;
  handleTouchStart: (event: TouchEvent, params: HandleTouchStartParams) => void;
  handleTouchMove: (event: TouchEvent) => void;
  handleTouchEnd: () => void;
}

export const useCalendarState = (stylistData: StylistData, business: BusinessData): CalendarStateReturn => {
  const [startDayOffset, setStartDayOffset] = useState<number>(0);
  const [calendar, setCalendar] = useState<boolean[][]>([]);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [dragValue, setDragValue] = useState<boolean | null>(null);
  const [activeDayIndex, setActiveDayIndex] = useState<number | null>(null);
  const [selectedSlots, setSelectedSlots] = useState<SelectedSlots>({ start: null, end: null });
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
  const [confirmRearrange, setConfirmRearrange] = useState<RearrangeConfirmation | null>(null);


  const getSlotIndex = useCallback(({ date, timeString }: GetSlotIndexParams): number => {
    const startHour = stylistData.availability?.startHour || business.businessHours.startHour;
    const [startHours, startMinutes] = startHour.split(':').map(Number);
    const startTotalMinutes = (startHours * 60) + startMinutes;

    let time: string;
    if (date) {
      time = format(date instanceof Date ? date : new Date(date), 'HH:mm');
    } else if (timeString) {
      time = timeString;
    } else {
      time = '00:00';
    }

    const [hours, minutes] = time.split(':').map(Number);
    const timeTotalMinutes = (hours * 60) + minutes;
    return Math.floor((timeTotalMinutes - startTotalMinutes) / 15);
  }, [stylistData, business]);

  const startSlot = getSlotIndex({ timeString: stylistData.availability?.startHour || business.businessHours.startHour });
  const endSlot = getSlotIndex({ timeString: stylistData.availability?.endHour || business.businessHours.endHour });

  const getSlotDate = useCallback((slotIndex: number, dayIndex: number): Date => {
    const startHour = stylistData.availability?.startHour || business.businessHours.startHour;
    const [startHours, startMinutes] = startHour.split(':').map(Number);
    const totalMinutes = (slotIndex * 15) + (startHours * 60) + startMinutes;
    const hours = Math.floor(totalMinutes / 60);
    const mins = totalMinutes % 60;
    const date = new Date();
    date.setDate(date.getDate() + dayIndex + startDayOffset);
    date.setHours(hours, mins, 0, 0);
    // console.log('date in getSlotDate', date);
    return date;
  }, [startDayOffset, stylistData, business]);

  const resetCalendarByStylistData = useCallback(() => {
    const openTimeSlots = stylistData.availability.timeSlots.filter(slot => slot.isOpen);
    const slotsPerDay = endSlot - startSlot;
    const newCalendar: boolean[][] = Array(3).fill(null).map(() => Array(slotsPerDay).fill(!stylistData.availability.isDefaultClose) as boolean[]);
    openTimeSlots.forEach(slot => {
      const dayIndex = new Date(slot.start).getDate() - new Date().getDate() - startDayOffset;
      if (dayIndex >= 0 && dayIndex < newCalendar.length) {
        const startSlotIndex = getSlotIndex({ date: slot.start });
        const endSlotIndex = getSlotIndex({ date: slot?.end ? new Date(new Date(slot.end as string).getTime() - 15 * 60 * 1000) : undefined });
        for (let i = startSlotIndex; i <= endSlotIndex; i++) {
          newCalendar[dayIndex][i] = true;
        }
      }
    });
    setCalendar(newCalendar);
  }, [startDayOffset, getSlotIndex, stylistData, endSlot, startSlot]);

  useEffect(() => {
    resetCalendarByStylistData();
  }, [stylistData, resetCalendarByStylistData]);

  const resetCalendar = useCallback(() => {
    resetCalendarByStylistData();
  }, [resetCalendarByStylistData]);

  const getReservation = useCallback((dayIndex: number, slotIndex: number, reservationData: Reservation[]): Reservation | undefined => {
    const currentDate = new Date();
    currentDate.setDate(currentDate.getDate() + dayIndex + startDayOffset);
    const slotTime = formatTime(slotIndex, stylistData.availability?.startHour || business.businessHours.startHour);

    return reservationData.find(r =>
      isSameDay(currentDate, new Date(r.start)) &&
      slotTime >= getTimeFormat(r.start) &&
      slotTime < getTimeFormat(r.end)
    );
  }, [startDayOffset, stylistData, business]);

  const getSlotBlock = useCallback((dayIndex: number, slotIndex: number): TimeSlot | null => {
    const currentDate = new Date();
    currentDate.setDate(currentDate.getDate() + dayIndex + startDayOffset);
    const slotTime = formatTime(slotIndex, stylistData.availability?.startHour || business.businessHours.startHour);

    const slotBlock = stylistData.availability.timeSlots.find(slot => {
      return isSameDay(currentDate, new Date(slot.start)) &&
        slotTime >= getTimeFormat(slot.start) &&
        slotTime < getTimeFormat(slot.end as string | Date);
    });

    return slotBlock ? slotBlock : null;
  }, [startDayOffset, stylistData, business]);

  const getDayOff = useCallback((dayIndex: number, startDayOffset: number): boolean => {
    const currentDate = new Date();
    currentDate.setDate(currentDate.getDate() + dayIndex + startDayOffset);
    const isDayOff = stylistData.availability.leaveDates.includes(format(currentDate, 'yyyy-MM-dd'));
    const isUsualDayOff = stylistData.availability.dayoff.includes(currentDate.getDay());
    return isDayOff || isUsualDayOff;
  }, [stylistData]);

  const fillDiscontinuousSelectedSlots = useCallback((dayIndex: number, startSlotIndex: number, endSlotIndex: number) => {
    const newCalendar = [...calendar];
    for (let i = startSlotIndex; i <= endSlotIndex; i++) {
      newCalendar[dayIndex][i] = true;
    }
    setCalendar(newCalendar);
  }, [calendar, setCalendar]);

  const resetSelectedSlots = (): void => {
    setSelectedSlots({ start: null, end: null });
  };

  const handleMouseDown = useCallback(({ dayIndex, slotIndex, reservation, slotBlock, isDayOff, rearrangeMode, rearrangingReservation, reservationData }: HandleMouseDownParams): HandleMouseDownResult => {
    const isReserved = !!reservation;
    const isSlotBlock = !!slotBlock;

    if (rearrangeMode) {
      if (isReserved) return null;

      const newStart = new Date(getSlotDate(slotIndex, dayIndex).setSeconds(0, 0));
      const originalDuration = (new Date(rearrangingReservation!.end).getTime() - new Date(rearrangingReservation!.start).getTime()) / (1000 * 60);
      const newEnd = new Date(newStart);
      newEnd.setMinutes(newEnd.getMinutes() + originalDuration);

      if (hasReservationConflict(reservationData, rearrangingReservation!, newStart, newEnd)) {
        return { error: 'Cannot rearrange to this time slot - conflicts with existing reservation' };
      }

      const hasStylistTimeSlotConflict = stylistData.availability.timeSlots.some(slot => {
        if (!isSameDay(newStart, new Date(slot.start))) return false;

        if (slot.isOpen) return false; //only check for closed time slots

        return !(newStart > new Date(new Date(slot.end as string).setSeconds(0, 0)) || newEnd < new Date(new Date(slot.start as string).setSeconds(0, 0)));
      });

      if (hasStylistTimeSlotConflict) {
        return { error: 'Cannot rearrange to this time slot - conflicts with blocked time slot' };
      }

      const endTimeDate = new Date(newEnd.setSeconds(0, 0));
      const [endHours, endMinutes] = stylistData.availability.endHour.split(':').map(Number);
      endTimeDate.setHours(endHours, endMinutes, 0, 0);
      if (!(isEqual(newEnd, endTimeDate) || isBefore(newEnd, endTimeDate))) {
        return { error: 'Cannot rearrange to this time slot - will over time' };
      }

      setConfirmRearrange({ newStart, newEnd });
      return null;
    }

    if (isReserved) {
      return { type: 'reservation', reservation: reservation! };
    }

    if (isDayOff) return null;

    if (isSlotBlock) {
      return { type: 'slotBlock', slotBlock: slotBlock! };
    }

    // Handle normal slot selection
    const newValue = !calendar[dayIndex][slotIndex];
    const newCalendar = [...calendar];
    newCalendar[dayIndex] = [...calendar[dayIndex]];
    newCalendar[dayIndex][slotIndex] = newValue;
    setCalendar(newCalendar);

    setIsDragging(true);
    setActiveDayIndex(dayIndex);
    setDragValue(newValue);

    const newSelectedSlots: SelectedSlots = {
      start: getSlotDate(slotIndex, dayIndex),
      end: new Date(getSlotDate(slotIndex, dayIndex).getTime() + 15 * 60 * 1000)
    };

    setSelectedSlots(newSelectedSlots);

    return { type: 'drag' };
  }, [calendar, getSlotDate, stylistData, setConfirmRearrange, setCalendar, setIsDragging, setActiveDayIndex, setDragValue, setSelectedSlots]);

  const handleMouseEnter = useCallback(({ dayIndex, slotIndex, isReserved, rearrangeMode }: HandleMouseEnterParams): void => {
    if (isReserved || rearrangeMode || !isDragging || dragValue === null || dayIndex !== activeDayIndex) return;

    const newCalendar = [...calendar];
    newCalendar[dayIndex] = [...calendar[dayIndex]];
    newCalendar[dayIndex][slotIndex] = dragValue;
    setCalendar(newCalendar);

    setSelectedSlots(prev => ({
      start: prev.start,
      end: getSlotDate(slotIndex + 1, dayIndex)
    }));
  }, [calendar, getSlotDate, setCalendar, setSelectedSlots, activeDayIndex, isDragging, dragValue]);

  const handleMouseUp = useCallback((): void => {
    if (isDragging && dragValue !== null) {
      let startSlotIndex = getSlotIndex({ date: selectedSlots.start });
      let endSlotIndex = getSlotIndex({ date: new Date(selectedSlots.end!.getTime() - 15 * 60 * 1000) });

      // Swap if start is greater than end
      if (startSlotIndex > endSlotIndex) {
        [startSlotIndex, endSlotIndex] = [endSlotIndex, startSlotIndex];
        setSelectedSlots(prev => ({
          start: prev.end,
          end: prev.start
        }));
      }

      fillDiscontinuousSelectedSlots(activeDayIndex!, startSlotIndex, endSlotIndex);
      setShowCreateModal(true);
    }
    setIsDragging(false);
    setDragValue(null);
  }, [isDragging, dragValue, activeDayIndex, selectedSlots, fillDiscontinuousSelectedSlots, setShowCreateModal, getSlotIndex, setSelectedSlots]);

  const handleTouchStart = useCallback((event: TouchEvent, { dayIndex, slotIndex, reservation, slotBlock, isDayOff, rearrangeMode, rearrangingReservation, reservationData }: HandleTouchStartParams): void => {
    setIsDragging(true);
    handleMouseDown({ dayIndex, slotIndex, reservation, slotBlock, isDayOff, rearrangeMode, rearrangingReservation, reservationData });
  }, [handleMouseDown]);

  const handleTouchMove = useCallback((event: TouchEvent): void => {
    if (isDragging) {
      event.preventDefault();
      const touch = event.touches[0];
      const target = document.elementFromPoint(touch.clientX, touch.clientY) as HTMLElement | null;
      if (target && target.dataset.slotIndex && target.dataset.dayIndex) {
        const slotIndex = parseInt(target.dataset.slotIndex, 10);
        const dayIndex = parseInt(target.dataset.dayIndex, 10);
        handleMouseEnter({ dayIndex, slotIndex, isReserved: false, rearrangeMode: false });
      }
    }
  }, [handleMouseEnter, isDragging]);

  const handleTouchEnd = useCallback((): void => {
    setIsDragging(false);
    handleMouseUp();
  }, [handleMouseUp]);

  useEffect(() => {
    const options: AddEventListenerOptions = { passive: false };

    window.addEventListener('mouseup', handleMouseUp);
    window.addEventListener('touchend', handleTouchEnd, options);
    window.addEventListener('touchmove', handleTouchMove as EventListener, options);
    window.addEventListener('touchstart', handleTouchStart as EventListener, options);

    return () => {
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('touchend', handleTouchEnd, options);
      window.removeEventListener('touchmove', handleTouchMove as EventListener, options);
      window.removeEventListener('touchstart', handleTouchStart as EventListener, options);
    };
  }, [handleMouseUp, handleTouchEnd, handleTouchMove, handleTouchStart]);

  return {
    startDayOffset,
    setStartDayOffset,
    calendar,
    setCalendar,
    getSlotIndex,
    getSlotDate,
    resetCalendarByStylistData,
    resetCalendar,
    getReservation,
    getSlotBlock,
    fillDiscontinuousSelectedSlots,
    isDragging,
    selectedSlots,
    showCreateModal,
    setShowCreateModal,
    resetSelectedSlots,
    handleMouseDown,
    handleMouseEnter,
    handleMouseUp,
    getDayOff,
    confirmRearrange,
    setConfirmRearrange,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
  };
};
